import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { api, newKey } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav, type Drawer } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { useClient, useClients, useMe, useRefresh, useServices, type ClientRow } from '../lib/data.ts';
import { addDays, agoLabel, dayLabel, DOW_SHORT, dowIndex, durLabel, first, hhmm, money, moneyShort, nowLocal, toMin } from '../lib/format.ts';
import { Avatar } from '../ui/bits.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { TimeSelect } from '../ui/TimeSelect.tsx';

type Props = Extract<Drawer, { kind: 'novo' }>;
interface Avail { professionals: Array<{ id: string; name: string; slots: string[] }> }

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function NovoSheet(p: Props) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useRefresh();
  const me = useMe().data!;
  const services = (useServices().data ?? []).filter((s) => s.active);
  const today = nowLocal().date;
  const key = useRef(newKey()).current;
  const resched = p.rescheduleId;
  const gap = !!(p.date && p.time && !resched);
  const [step, setStep] = useState(resched ? 3 : p.clientId ? 2 : 1);
  const [query, setQuery] = useState('');
  const [clientId, setClientId] = useState<string | null>(p.clientId ?? null);
  const [isNew, setIsNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newWhats, setNewWhats] = useState(false);
  // o primeiro serviço escolhido é o principal; os outros somam no mesmo horário
  const [svcIds, setSvcIds] = useState<string[]>([]);
  const svcId = svcIds[0] ?? null;
  const setSvcId = (id: string) => setSvcIds([id]);
  const toggleSvc = (id: string) => setSvcIds((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const [pro, setPro] = useState<string>(p.professionalId ?? 'any');
  const [date, setDate] = useState(p.date ?? today);
  const [time, setTime] = useState<string | null>(p.time ?? null);
  const [askConfirm, setAskConfirm] = useState(true);
  const [wait, setWait] = useState(false);
  // repetir toda semana (ou a cada 2 ou 4) e quantas vezes
  const [every, setEvery] = useState(0);
  const [times, setTimes] = useState(8);
  // outra parceira ao mesmo tempo (ex.: mão e pé juntas)
  const [withPro, setWithPro] = useState<string | null>(null);
  const [withSvc, setWithSvc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const clients = useClients(query, 'all');
  const cli = useClient(clientId ?? undefined).data;
  const rescAppt = useQuery({ queryKey: ['appt', resched], queryFn: () => api.get<{ serviceId: string; date: string; time: string; client: { id: string; name: string }; extras: Array<{ serviceId: string }> }>(`/appointments/${resched}`), enabled: !!resched });
  const effectiveSvc = resched ? rescAppt.data?.serviceId ?? null : svcId;
  const svc = services.find((s) => s.id === effectiveSvc) ?? null;
  const extraIds = resched ? (rescAppt.data?.extras ?? []).map((x) => x.serviceId) : svcIds.slice(1);
  const extraSvcs = extraIds.map((id) => services.find((s) => s.id === id)).filter((s): s is NonNullable<typeof s> => !!s);
  const totalMin = (svc?.durationMin ?? 0) + extraSvcs.reduce((t, s) => t + s.durationMin, 0);
  const totalPrice = (svc?.priceCents ?? 0) + extraSvcs.reduce((t, s) => t + s.priceCents, 0);
  const svcNames = [svc?.name, ...extraSvcs.map((s) => s.name)].filter(Boolean).join(' + ');
  const avail = useQuery({
    queryKey: ['availability', date, effectiveSvc, extraIds.join(','), resched ?? ''],
    queryFn: () => api.get<Avail>(`/availability?date=${date}&serviceId=${effectiveSvc}${extraIds.length ? `&extraServiceIds=${extraIds.join(',')}` : ''}${resched ? `&ignore=${resched}` : ''}`),
    enabled: !!effectiveSvc && step >= 3,
  });
  const lastSvcName = cli?.history.find((h) => h.status === 'completed')?.service;
  const usual = services.find((s) => s.name === lastSvcName) ?? null;
  const noShows = cli?.history.filter((h) => h.status === 'no_show').length ?? 0;
  const rules = me.salon.settings.deposit;
  const needsDeposit = !isNew && (rules.mode === 'always' || (rules.mode === 'after_no_shows' && noShows >= rules.afterNoShows));
  const deposit = svc && needsDeposit ? (rules.kind === 'fixed' ? Math.min(rules.fixedCents, totalPrice) : Math.round((totalPrice * rules.bps) / 10_000 / 100) * 100) : 0;
  const whoName = isNew ? newName : cli?.name ?? rescAppt.data?.client.name ?? '';
  const whats = isNew ? newWhats : !!cli?.whatsappConsentAt;

  // sobreposição liberada nas Regras: qualquer horário vale, mesmo ocupado
  const allowOverlap = !!me.salon.settings.allowOverlap;
  // quem atende no horário escolhido
  const slotPro = (t: string) => {
    const ps = avail.data?.professionals ?? [];
    const free = pro !== 'any'
      ? ps.find((x) => x.id === pro && x.slots.includes(t))?.id ?? null
      : (ps.find((x) => x.id === me.professionalId && x.slots.includes(t)) ?? ps.find((x) => x.slots.includes(t)))?.id ?? null;
    if (free || !allowOverlap) return free;
    if (pro !== 'any') return pro;
    return ps.some((x) => x.id === me.professionalId) ? me.professionalId : ps[0]?.id ?? null;
  };
  const slots = useMemo(() => {
    const ps = avail.data?.professionals ?? [];
    const set = new Set<string>();
    ps.filter((x) => pro === 'any' || x.id === pro).forEach((x) => x.slots.forEach((t) => set.add(t)));
    return [...set].sort();
  }, [avail.data, pro]);

  const total = resched ? 2 : gap ? 3 : 4;
  const cur = resched ? step - 2 : gap && step === 4 ? 3 : step;
  const title = resched ? 'Remarcar horário' : gap ? 'Encaixe' : 'Novo agendamento';
  const bits: string[] = [];
  if (step === 1 && isNew) bits.push('Cliente nova');
  if (step >= 2 && whoName) bits.push(whoName);
  if (gap) bits.push(`${dayLabel(date, today)} ${p.time}`);
  if (step >= 3 && svc) bits.push(svcNames);
  if (step >= 4 && time) bits.push(`${dayLabel(date, today)} ${time}`);

  const back = step > (resched ? 3 : p.clientId ? 2 : 1) ? () => setStep(gap && step === 4 ? 2 : step - 1) : undefined;

  async function confirm() {
    if (!svc || !time) return;
    const proId = slotPro(time);
    if (!proId) { toast('Esse horário acabou de ser ocupado. Escolha outro.', 'warn'); setStep(3); return; }
    setBusy(true);
    try {
      if (resched) {
        await api.post(`/appointments/${resched}/reschedule`, { date, time, professionalId: proId });
        await refresh();
        toast(`${first(whoName)} remarcada para ${dayLabel(date, today)} às ${time}.`);
        nav.close();
        return;
      }
      let cid = clientId;
      if (isNew) {
        const c = await api.post<{ id: string }>('/clients', { name: newName.trim(), phone: newPhone });
        cid = c.id;
        if (newWhats) await api.post(`/clients/${cid}/consent`, { whatsapp: true });
      }
      const made = await api.post<{ series?: { created: string[]; skipped: Array<{ date: string; reason: string }> } }>('/appointments', {
        clientId: cid, professionalId: proId, serviceId: svc.id, date, time, idempotencyKey: key,
        ...(extraIds.length ? { extraServiceIds: extraIds } : {}),
        depositCents: deposit, askConfirmation: whats && askConfirm,
        ...(every ? { repeat: { everyWeeks: every, count: times } } : {}),
        ...(withPro && withSvc ? { with: { professionalId: withPro, serviceId: withSvc } } : {}),
      });
      if (made.series) {
        const sk = made.series.skipped;
        await refresh();
        toast(`${first(whoName)} agendada ${made.series.created.length} vezes, a partir de ${dayLabel(date, today)} às ${time}.${sk.length ? ` Ficaram de fora (horário ocupado): ${sk.map((x) => x.date.slice(8) + '/' + x.date.slice(5, 7)).join(', ')}.` : ''}`, sk.length ? 'warn' : undefined);
        nav.close();
        return;
      }
      // lista de espera: quer um horário antes deste, se alguém desmarcar
      if (wait && date > today) await api.post('/waitlist', { clientId: cid, serviceId: svc.id, professionalId: pro === 'any' ? null : proId, dateFrom: today, dateTo: addDays(date, -1) }).catch(() => toast('Agendado, mas não entrou na lista de espera. Tente de novo pela Lista de espera.', 'warn'));
      await refresh();
      toast(`${first(whoName)} agendada ${dayLabel(date, today)} às ${time}.${deposit ? ` Sinal de ${money(deposit)} aguardando o Pix.` : ''}`);
      nav.close();
    } catch (e) { toast(msg(e), 'warn'); await refresh(); } finally { setBusy(false); }
  }

  let body: React.ReactNode = null;
  let actions: React.ReactNode = null;
  if (step === 1 && !isNew) {
    const list = (clients.data ?? []) as ClientRow[];
    const sorted = query ? list : [...list].sort((a, b) => dueScore(b) - dueScore(a));
    body = (
      <>
        <div className="search-in"><Icon name="search" /><input autoFocus type="search" placeholder="Nome ou telefone" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Buscar cliente" /></div>
        <button className="cl-row new top" onClick={() => { setIsNew(true); setNewName(/\d/.test(query) ? '' : query); setNewPhone(/\d/.test(query) ? query : ''); }}>
          <span className="av t-new"><Icon name="plus" /></span><span><strong>Cliente nova</strong><small>Cadastrar agora, em 2 campos</small></span><Icon name="chev" />
        </button>
        <div className="cl-rows">
          {sorted.slice(0, 80).map((c, i) => (
            <div key={c.id} style={{ display: 'contents' }}>
              {!query && sorted.length > 5 && i === 0 && <p className="cl-label">Costumam vir por agora</p>}
              {!query && sorted.length > 5 && i === 3 && <p className="cl-label">Outras clientes</p>}
              <button className={'cl-row' + (clientId === c.id ? ' sel' : '')} onClick={() => { setClientId(c.id); setStep(2); }}>
                <Avatar id={c.id} name={c.name} />
                <span><strong>{c.name}</strong><small>{c.last_visit ? `${agoLabel(c.days_away ?? 0)} · ${c.last_service ?? ''}` : `${c.phone || 'sem celular'} · ${c.visits ? `${c.visits} ${c.visits === 1 ? 'visita' : 'visitas'}` : 'primeira vez'}`}</small></span>
                <Icon name="chev" />
              </button>
            </div>
          ))}
        </div>
        {query && !clients.isFetching && !list.length && <div className="note"><Icon name="info" /><span>Ninguém encontrado. Cadastre como cliente nova.</span></div>}
      </>
    );
  } else if (step === 1 && isNew) {
    const dup = (clients.data ?? []).find((c) => newPhone.replace(/\D/g, '').length >= 10 && c.phone.replace(/\D/g, '').endsWith(newPhone.replace(/\D/g, '').slice(-8)));
    body = (
      <>
        <div className="field"><label htmlFor="nbName">Nome</label><input className="text-in" id="nbName" autoFocus autoComplete="off" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Como ela se chama" /></div>
        <div className="field"><label htmlFor="nbPhone">WhatsApp</label><input className="text-in" id="nbPhone" type="tel" inputMode="tel" autoComplete="off" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="(48) 90000-0000" /></div>
        {dup && <div className="note warn"><Icon name="info" /><span>Esse celular já é da {dup.name}. <button className="gl-act" onClick={() => { setIsNew(false); setClientId(dup.id); setStep(2); }}>Usar a ficha dela</button></span></div>}
        <div className="toggle-line"><span><strong>Autorizou mensagens no WhatsApp</strong><small>Pergunte à cliente. O sistema não presume.</small></span>
          <button role="switch" aria-checked={newWhats} aria-label="Autorizou mensagens" onClick={() => setNewWhats(!newWhats)}><Icon name={newWhats ? 'toggleOn' : 'toggleOff'} /></button></div>
      </>
    );
    actions = <><button className="cta" disabled={newName.trim().length < 2 || !!dup} onClick={() => setStep(2)}>Continuar</button><button className="link-back" onClick={() => setIsNew(false)}>Voltar para a lista</button></>;
  } else if (step === 2) {
    body = (
      <>
        {usual && !gap && (
          <>
            <div className="usual"><span className="due-label">Do jeito de sempre</span><strong>{usual.name}</strong>
              <small>{durLabel(usual.durationMin)} · {moneyShort(usual.priceCents)}</small>
              <div className="usual-slots"><button onClick={() => { setSvcId(usual.id); setStep(3); }}><b>Escolher horário</b></button></div>
            </div>
            <div className="section-title">Ou escolha outro serviço (pode somar vários)</div>
          </>
        )}
        {!usual && <div className="section-title" style={{ marginTop: 0 }}>Serviço · toque em mais de um para somar</div>}
        {services.map((s) => (
          <button key={s.id} className={'pick' + (svcIds.includes(s.id) ? ' sel' : '')} aria-pressed={svcIds.includes(s.id)} onClick={() => toggleSvc(s.id)}>
            <span className="radio" /><span><strong>{s.name}</strong><small>{durLabel(s.durationMin)}{s.cleanupMin ? ` + ${s.cleanupMin} min de limpeza` : ''}</small></span>
            <span className="price">{s.priceFrom ? 'a partir de ' : ''}{moneyShort(s.priceCents)}</span>
          </button>
        ))}
      </>
    );
    actions = <>
      {svcIds.length > 1 && <p className="m-hint" style={{ textAlign: 'center' }}>{svcIds.length} procedimentos · {durLabel(totalMin)} · {moneyShort(totalPrice)}</p>}
      <button className="cta" disabled={!svcId} onClick={() => setStep(gap ? 4 : 3)}>{gap ? `Encaixar ${first(whoName)} às ${p.time}` : 'Escolher horário'}</button>
    </>;
  } else if (step === 3) {
    const ps = avail.data?.professionals ?? [];
    const hrs = me.salon.settings.hours[(['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'] as const)[dowIndex(date)]!];
    const tFrom = allowOverlap || !hrs?.on ? '06:00' : hrs.ini, tTo = allowOverlap || !hrs?.on ? '23:55' : hrs.fim;
    const days = Array.from({ length: 21 }, (_, i) => addDays(today, i));
    body = (
      <>
        {resched && rescAppt.data && <div className="note" style={{ marginTop: 0 }}><Icon name="info" /><span>Hoje marcado para {dayLabel(rescAppt.data.date, today)} às {rescAppt.data.time}. Escolha o novo horário.</span></div>}
        <div className="section-title" style={{ marginTop: resched ? 14 : 0 }}>Com quem</div>
        <div className="chipset pro-pick">
          {[{ id: 'any', name: 'Quem estiver livre' }, ...ps].map((x) => (
            <button key={x.id} className={pro === x.id ? 'on' : ''} aria-pressed={pro === x.id} onClick={() => { setPro(x.id); setTime(null); }}>{x.id === me.professionalId ? 'Você' : x.name}</button>
          ))}
        </div>
        <div className="section-title">Dia</div>
        <div className="days-row">
          {days.map((d) => (
            <button key={d} className={date === d ? 'sel' : ''} onClick={() => { setDate(d); setTime(null); }}>
              <small>{d === today ? 'hoje' : DOW_SHORT[dowIndex(d)]!.toLowerCase()}</small><strong>{Number(d.slice(8))}</strong>
            </button>
          ))}
        </div>
        <div className="section-title">Horário · {svc ? durLabel(totalMin) : ''}</div>
        {avail.isLoading ? <p className="m-hint">Procurando horários…</p> : slots.length || allowOverlap ? (
          <>
            <TimeSelect label="Horário" value={time} onChange={setTime} free={slots} from={tFrom} to={tTo} allowBusy={allowOverlap} />
            {allowOverlap && time && !slots.includes(time) && <p className="m-hint">Já tem alguém neste horário. O novo horário fica sobreposto.</p>}
          </>
        ) : (
          <div className="wempty slot-none"><span>Sem horário livre {date === today ? 'hoje' : 'neste dia'}{pro === 'any' ? '' : ` com ${ps.find((x) => x.id === pro)?.name ?? ''}`}.</span>
            <button onClick={() => { setDate(addDays(date, 1)); setTime(null); }}>Ver o dia seguinte</button></div>
        )}
      </>
    );
    // o botão já é o resumo e agenda direto; repetir, confirmação e outra parceira ficam em "mais opções"
    actions = <>
      <button className="cta" disabled={!time || busy || !slotPro(time)} onClick={confirm}>
        {busy ? 'Salvando…' : !time ? 'Escolha o horário' : `${resched ? 'Remarcar para' : `Agendar ${first(whoName)} ·`} ${dayLabel(date, today)} às ${time}`}</button>
      {!resched && time && <button className="link-back" onClick={() => setStep(4)}>Mais opções: repetir, confirmação, outra parceira</button>}
    </>;
  } else if (svc && time) {
    const proId = slotPro(time);
    const proName = avail.data?.professionals.find((x) => x.id === proId)?.name;
    body = (
      <>
        <div className="review">
          <div><span>Cliente</span><b>{whoName}{isNew ? ' · nova' : ''}</b></div>
          <div><span>{extraSvcs.length ? 'Serviços' : 'Serviço'}</span><b>{svcNames} · {moneyShort(totalPrice)}</b></div>
          <div><span>Quando</span><b>{dayLabel(date, today)} · {time} às {hhmm(toMin(time) + totalMin)}</b></div>
          <div><span>Com</span><b>{proId === me.professionalId ? 'você' : proName ?? '—'}{pro === 'any' && proId ? ' (livre)' : ''}</b></div>
          {deposit > 0 && <div><span>Sinal</span><b>{money(deposit)} pelo link · {rules.mode === 'always' ? 'regra do salão' : `${noShows} ${noShows > 1 ? 'faltas' : 'falta'}`}</b></div>}
        </div>
        {!resched && (
          <div className="toggle-line"><span><strong>Pedir confirmação pelo WhatsApp</strong><small>{whats ? 'O horário fica "a confirmar" até ela responder.' : `${first(whoName)} não autorizou mensagens. O horário já entra confirmado.`}</small></span>
            <button role="switch" aria-checked={askConfirm && whats} aria-label="Pedir confirmação pelo WhatsApp" disabled={!whats} onClick={() => setAskConfirm(!askConfirm)}><Icon name={askConfirm && whats ? 'toggleOn' : 'toggleOff'} /></button></div>
        )}
        {!resched && !withPro && (
          <>
            <p className="gl-title">Repetir</p>
            <div className="chipset soft">{([[0, 'Não repete'], [1, 'Toda semana'], [2, 'A cada 2 semanas'], [4, 'A cada 4 semanas']] as const).map(([k, l]) => <button key={k} className={every === k ? 'on' : ''} aria-pressed={every === k} onClick={() => setEvery(k)}>{l}</button>)}</div>
            {every > 0 && <div className="chipset soft" style={{ marginTop: 6 }}>{[4, 8, 12, 26].map((n) => <button key={n} className={times === n ? 'on' : ''} aria-pressed={times === n} onClick={() => setTimes(n)}>{n} vezes</button>)}</div>}
            {every > 0 && <p className="m-hint">Os dias em que o horário já estiver ocupado ficam de fora, e o app avisa quais.</p>}
          </>
        )}
        {!resched && !every && (avail.data?.professionals.length ?? 0) > 1 && (
          <>
            <div className="toggle-line"><span><strong>Outra parceira ao mesmo tempo</strong><small>Ex.: mão e pé juntas. As duas entram na agenda, ou nenhuma.</small></span>
              <button role="switch" aria-checked={!!withPro} aria-label="Outra parceira ao mesmo tempo" onClick={() => { setWithPro(withPro ? null : (avail.data!.professionals.find((x) => x.id !== proId)?.id ?? null)); setWithSvc(null); }}><Icon name={withPro ? 'toggleOn' : 'toggleOff'} /></button></div>
            {withPro && <>
              <div className="chipset soft">{avail.data!.professionals.filter((x) => x.id !== proId).map((x) => <button key={x.id} className={withPro === x.id ? 'on' : ''} aria-pressed={withPro === x.id} onClick={() => setWithPro(x.id)}>{x.id === me.professionalId ? 'Você' : x.name}</button>)}</div>
              <div className="chipset soft" style={{ marginTop: 6 }}>{services.filter((x) => x.id !== svc.id).map((x) => <button key={x.id} className={withSvc === x.id ? 'on' : ''} aria-pressed={withSvc === x.id} onClick={() => setWithSvc(x.id)}>{x.name}</button>)}</div>
              {!withSvc && <p className="m-hint">Escolha o serviço da outra parceira.</p>}
            </>}
          </>
        )}
        {!resched && date > today && !every && (
          <div className="toggle-line"><span><strong>Quer um horário antes, se abrir?</strong><small>Entra na lista de espera e é avisada se alguém desmarcar.</small></span>
            <button role="switch" aria-checked={wait} aria-label="Lista de espera" onClick={() => setWait(!wait)}><Icon name={wait ? 'toggleOn' : 'toggleOff'} /></button></div>
        )}
      </>
    );
    actions = <button className="cta" disabled={busy || !proId || (!!withPro && !withSvc)} onClick={confirm}>{busy ? 'Salvando…' : resched ? `Remarcar para ${time}` : `Agendar ${dayLabel(date, today)} às ${time}`}</button>;
  }
  // encaixe: se o horário escolhido não cabe para o serviço, volta para a escolha de horário
  if (gap && step === 4 && svc && avail.data && time && !slotPro(time)) {
    body = <div className="note warn"><Icon name="info" /><span>{svc.name} não cabe às {time}. Escolha outro horário.</span></div>;
    actions = <button className="cta" onClick={() => { setTime(null); setStep(3); }}>Ver horários livres</button>;
  }
  if (gap && step === 4 && !avail.data && svc) {
    return <Sheet title={title} steps={[cur, total]} sub={bits.join(' · ')}><p className="m-hint">Conferindo o horário…</p></Sheet>;
  }
  return <Sheet title={title} onBack={back} steps={[cur, total]} sub={bits.join(' · ') || undefined} actions={actions}>{body}</Sheet>;
}

// clientes com retorno vencendo aparecem primeiro ("costumam vir por agora")
function dueScore(c: ClientRow): number {
  if (!c.last_visit || !c.return_days || c.has_future) return -1;
  const late = (c.days_away ?? 0) - c.return_days;
  return late >= -3 ? 100 - Math.abs(late) : -1;
}
